import {
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { cn } from '@/shared/lib/cn';
import { BACK_LINK, FIELD, FIELD_ERROR, LABEL } from './styles';

/**
 * Form pieces shared by sign-in, the second factor and password reset, built
 * the way the admin console's sign-in form is.
 */

/** A banner for anything that is not about one field - the admin's FormBanner. */
export function FormError({ message, requestId }: { message: string | null; requestId?: string }) {
  if (!message) return null;
  return (
    <p
      role="alert"
      className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
    >
      {message}
      {/* What support uses to find this exact request in the server logs. */}
      {requestId && (
        <span className="mt-1 block font-mono text-[11px] opacity-75">Reference: {requestId}</span>
      )}
    </p>
  );
}

export function FieldError({ id, message }: { id: string; message: string | undefined }) {
  if (!message) return null;
  return (
    <p id={id} className={FIELD_ERROR}>
      {message}
    </p>
  );
}

/** A labelled text input, wired to aria so a screen reader announces its error. */
export function TextField({
  id,
  label,
  error,
  className,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & { id: string; label: string; error?: string }) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id} className={LABEL}>
        {label}
      </Label>
      <Input
        id={id}
        className={FIELD}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        {...input}
      />
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}

/**
 * A password input with a show/hide toggle.
 *
 * Reveal is local and never persisted: a password left legible across a
 * remount is a shoulder-surfing hazard, not a convenience.
 */
export function PasswordField({
  id,
  label,
  error,
  hint,
  labelAction,
  className,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  /** Sits at the right of the label row, where the admin form has "Forgot?". */
  labelAction?: ReactNode;
}) {
  const [revealed, setRevealed] = useState(false);
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <Label htmlFor={id} className={LABEL}>
          {label}
        </Label>
        {labelAction}
      </div>
      <div className="relative">
        <Input
          id={id}
          type={revealed ? 'text' : 'password'}
          // Room on the right so a long password never runs under the toggle.
          className={cn(FIELD, 'pr-11')}
          aria-invalid={Boolean(error)}
          aria-describedby={describedBy}
          {...input}
        />
        <button
          type="button"
          // Out of the tab order: tabbing out of a password should reach the
          // next field or the submit button, not a display preference.
          tabIndex={-1}
          onClick={() => setRevealed((shown) => !shown)}
          aria-label={revealed ? 'Hide password' : 'Show password'}
          aria-pressed={revealed}
          aria-controls={id}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-[9px] text-[#48536A]/60 transition-colors hover:text-[#48536A] hover:cursor-pointer"
        >
          {revealed ? <EyeOff className="size-4.5" /> : <Eye className="size-4.5" />}
        </button>
      </div>
      {error ? (
        <FieldError id={`${id}-error`} message={error} />
      ) : (
        hint && (
          <p id={`${id}-hint`} className="text-[12px] text-muted-foreground">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

/**
 * Six code boxes - six real inputs, as the admin MFA step has them.
 *
 * It used to be one invisible input laid over six drawn boxes. That is neat
 * with a plain keyboard, but password-manager extensions, IME and touch
 * keyboards all fight an invisible field, and in practice people could not
 * type into it. Six visible inputs is what the admin console ships and what
 * works everywhere; paste and one-time-code autofill are handled by hand below.
 *
 * `value` is the digits entered so far, as one string. Setting it to '' from
 * outside (after a wrong code) clears the boxes and returns focus to the first.
 */
export function CodeInput({
  id,
  value,
  onChange,
  onBlur,
  error,
  autoFocus,
  disabled,
  label,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  /** For screen readers; the boxes carry no visible label. */
  label: string;
}) {
  const length = 6;
  const [digits, setDigits] = useState<string[]>(() =>
    Array.from({ length }, (_, i) => value[i] ?? ''),
  );
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  /*
   * Cleared from outside - a wrong code - so the boxes empty too. Adjusted
   * during render, React's pattern for state that follows a prop, rather than
   * in an effect, which would paint the stale digits for a frame first.
   */
  const [lastValue, setLastValue] = useState(value);
  const [resets, setResets] = useState(0);
  if (value !== lastValue) {
    setLastValue(value);
    if (value === '' && digits.some(Boolean)) {
      setDigits(Array<string>(length).fill(''));
      setResets((count) => count + 1);
    }
  }

  // ...and focus goes back to the first box, which only an effect can do.
  useEffect(() => {
    if (resets > 0) inputs.current[0]?.focus();
  }, [resets]);

  const commit = (next: string[]) => {
    setDigits(next);
    onChange(next.join(''));
  };

  const setDigit = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    if (!digit && raw !== '') return;

    const next = [...digits];
    next[index] = digit;
    commit(next);

    if (digit && index < length - 1) inputs.current[index + 1]?.focus();
  };

  const onKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      const next = [...digits];
      next[index - 1] = '';
      commit(next);
      inputs.current[index - 1]?.focus();
    }
    if (event.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus();
    if (event.key === 'ArrowRight' && index < length - 1) inputs.current[index + 1]?.focus();
  };

  // Paste, and the browser's one-time-code autofill, deliver all six at once.
  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;

    event.preventDefault();
    commit(Array.from({ length }, (_, i) => pasted[i] ?? ''));
    inputs.current[Math.min(pasted.length, length - 1)]?.focus();
  };

  return (
    <div className="space-y-1.5">
      <div role="group" aria-label={label} className="flex gap-2.5">
        {digits.map((digit, index) => (
          <input
            key={index}
            id={index === 0 ? id : `${id}-${String(index + 1)}`}
            ref={(element) => {
              inputs.current[index] = element;
            }}
            value={digit}
            onChange={(event) => setDigit(index, event.target.value)}
            onKeyDown={onKeyDown(index)}
            onPaste={onPaste}
            // Moving between boxes is not leaving the field; only report a
            // blur once focus is outside the group, or validation would scold
            // somebody halfway through typing the code.
            onBlur={(event) => {
              const next = event.relatedTarget;
              if (!(next instanceof Node && event.currentTarget.parentElement?.contains(next))) {
                onBlur?.();
              }
            }}
            // The select-on-focus makes typing over a filled box replace it.
            onFocus={(event) => event.target.select()}
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            autoFocus={autoFocus && index === 0}
            disabled={disabled}
            aria-label={`Digit ${String(index + 1)}`}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${id}-error` : undefined}
            className={cn(
              'h-13 w-full rounded-[9px] border-[1.5px] border-input bg-background text-center font-mono text-xl font-semibold text-primary shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50',
              error && 'border-destructive',
            )}
          />
        ))}
      </div>
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}

/** The admin MFA step's quiet "← Back to sign in". */
export function BackLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={BACK_LINK}>
      ← {label}
    </button>
  );
}
